import { createContext, useState, useMemo, useEffect } from "react";

import { Table, Tag, Checkbox, App } from "antd";

import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import DragHandle from "../../ui/DragHandle";

import { updateField } from "@/api/definitionTableForm";

import { FIELD_TYPES_OPTIONS, DEFINITIONS } from "@/constants/definitions";

const RowContext = createContext({});

const Row = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });
  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
        }
      : {}),
  };
  const contextValue = useMemo(
    () => ({
      setActivatorNodeRef,
      listeners,
    }),
    [setActivatorNodeRef, listeners]
  );
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

// Таблица со всеми полями объекта (включая шаблонные). Для настройки отображения списка объектов.
// Полям можно задать порядок и отображение в таблице
// Тут можно настроить колонки
const TabularFormFieldsTable = ({
  data = [],
  pagination,
  onChange,
  onFieldChange,
}) => {
  const { message } = App.useApp();

  const [dataSource, setDataSource] = useState(data);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setDataSource(data);
  }, [data]);

  const columns = [
    {
      key: "sort",
      align: "center",
      width: 40,
      render: (_, field) => (
        <DragHandle context={RowContext} disabled={!field.is_visible} />
      ),
    },

    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      width: 250,
    },
    {
      key: "is_visible",

      // title: <EyeIcon style={{ fontSize: 16, verticalAlign: "middle" }} />,
      title: "Видимость",
      align: "center",
      dataIndex: "is_visible",
      width: 80,
      render: (is_visible, field) => {
        return (
          <Checkbox
            defaultChecked={is_visible}
            onChange={(e) => handleChangeVisible(e, field)}
          />
        );
      },
    },
    {
      title: "Тип",
      dataIndex: "type",
      key: "type",
      width: 150,
      render: (type, field) => {
        if (DEFINITIONS.includes(type)) {
          return <Tag>{field.object_name || field.uuid}</Tag>;
        } else {
          const t = FIELD_TYPES_OPTIONS.find((item) => item.value === type);
          return t ? <Tag color={t.color}>{t.label}</Tag> : <Tag>{type}</Tag>;
        }
      },
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
    },
  ];

  const handleChangeVisible = async (e, field) => {
    setLoading(true);
    const { checked } = e.target;
    try {
      await updateField({
        ...field,
        is_visible: checked,
      });
      await onFieldChange();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = async ({ active, over }) => {
    if (active?.id !== over?.id) {
      setLoading(true);

      const activeField = data.find((i) => i.uuid === active?.id);
      const activeIndex = data.findIndex((i) => i.uuid === active?.id);
      const overIndex = data.findIndex((i) => i.uuid === over?.id);
      const a = arrayMove(dataSource, activeIndex, overIndex);
      setDataSource([...a]);

      try {
        await updateField({
          ...activeField,
          idx: overIndex,
        });
        await onFieldChange();
      } catch (error) {
        message.open({
          type: "error",
          content: error.message,
          duration: 10,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
      <SortableContext
        items={dataSource.map((i) => i.uuid)}
        strategy={verticalListSortingStrategy}
      >
        <Table
          dataSource={dataSource}
          columns={columns}
          rowKey="uuid"
          loading={loading}
          pagination={pagination}
          onChange={onChange}
          showSorterTooltip={{
            target: "sorter-icon",
            title: "Нажмите для сортировки",
          }}
          size="small"
          scroll={{
            x: "100%",
          }}
          components={{
            body: {
              row: Row,
            },
          }}

          // footer={() => (
          //   <Button type="primary" icon={<PlusOutlined />} onClick={onAddItem}>
          //     Добавить
          //   </Button>
          // )}
        />
      </SortableContext>
    </DndContext>
  );
};

export default TabularFormFieldsTable;
